// public/js/modules/map/markers.js
import {
  markers
} from './state.js';
import { loadAssetHistoryAndDrawRoute } from './history.js';
import { typeColors, isValidLatLon } from './constants.js';
import { formatAssetLabel, getAssetTooltip } from '../../utils/asset-utils.js';
import { escapeHtml } from '../../utils/escape.js';
import { showAssetInfoPanel, setPanelHistoryStatus } from './info-panel.js';
import { getTemplate, renderTemplate, SURFACES, PIN_LABEL_HARD_MAX } from '../../features/display/template-store.js';

const ACTIVE_POSITION_HOURS = 4;

// The dot's centre sits 3px below the 90x50 element's centre, so every marker
// carries this nudge to put the DOT on the coordinate (see addAssetMarker).
const PIN_ANCHOR_NUDGE_Y = -3;

// Two assets parked in the same yard are a normal case in this product, not an
// edge case: Copa's Test 01 and Test 03 stand 3.9 m apart, which is ZERO pixels
// at any useful zoom. The pin drawn second covered the first completely, and the
// asset simply looked absent from the map (reported 2026-10-17).
//
// So markers whose dots land within SPREAD_MIN_PX of each other are fanned out
// vertically and each gets a hairline back to its true position. Vertically,
// because the label pill is 84px wide and 15px tall - a horizontal fan would
// need a 90px throw to stop the labels colliding, which would misplace the pin
// badly enough to be its own bug.
const SPREAD_MIN_PX = 26;   // closer than this counts as stacked
const SPREAD_PITCH_PX = 34; // vertical gap between fanned pins (dot 24px + air)
const SPREAD_MAX = 5;       // above this, fanning is clutter - clustering's job

// Resolve a pin's label through the configured template, falling back to the
// built-in asset label whenever the template is absent, empty or unresolvable.
function pinLabelFor(asset) {
  try {
    const tpl = getTemplate(SURFACES.pinLabel);
    const text = typeof tpl === 'string' ? tpl : tpl?.text;
    if (text && text.trim()) {
      const rendered = renderTemplate(text, asset, { plain: true, maxLength: PIN_LABEL_HARD_MAX });
      if (rendered) return rendered;
    }
  } catch (e) { /* fall through to the default label */ }
  return formatAssetLabel(asset);
}

export function addAssetMarker(asset, map) {
  if (!map || !isValidLatLon(asset.lastLat, asset.lastLon)) return null;

  const lng = parseFloat(asset.lastLon);
  const lat = parseFloat(asset.lastLat);

  if (markers.has(asset.assetUUID)) {
    try { markers.get(asset.assetUUID).remove(); } catch (e) {}
    markers.delete(asset.assetUUID);
  }

  // Judge "recent" by the last REAL fix when the asset has one: a device that
  // only heartbeats from a cached position must not light the pin green.
  const positionAt = asset.lastFixAt || asset.lastUpdated;
  const isRecentlyMoved = positionAt &&
    (Date.now() - new Date(positionAt).getTime()) < ACTIVE_POSITION_HOURS * 60 * 60 * 1000;

  const innerColor = isRecentlyMoved ? '#22c55e' : '#ffffff';
  const color = typeColors[asset.type] || typeColors.default;

  // Pin label follows the tenant's (or user's) pin-label template; the default
  // template reproduces the previous behaviour exactly. Kept short on purpose —
  // long labels overlap once pins cluster (docs/asset-display-templates.md).
  const rawLabel = pinLabelFor(asset);
  const displayLabel = rawLabel.length > 16 ? rawLabel.substring(0, 15) + '…' : rawLabel;

  // Build a rich DOM element for the marker (circle + label pill, similar to old Google SVG)
  const el = document.createElement('div');
  el.className = 'map-asset-marker';
  // IMPORTANT: do NOT set position here. MapLibre positions its markers with
  // `position:absolute` (via the .maplibregl-marker class) + a transform. An
  // inline `position:relative` overrides that, dropping the markers into normal
  // document flow — they then stack vertically and drift south (the "pins in a
  // column" bug, visible whenever the DOM-marker path is used, e.g. clustering
  // off or fewer than 4 assets). The inner absolute children still anchor to
  // this element because MapLibre makes it the positioned ancestor.
  // z-index keeps asset markers above any other DOM overlays.
  el.style.cssText = 'width:90px;height:50px;cursor:pointer;pointer-events:auto;z-index:5;';
  el.innerHTML = `
    <div style="position:absolute;left:50%;top:16px;transform:translateX(-50%);width:24px;height:24px;border-radius:9999px;background:${color};border:3px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.3);">
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:10px;height:10px;border-radius:9999px;background:${innerColor};"></div>
    </div>
    <div style="position:absolute;left:3px;top:31px;width:84px;height:15px;background:rgba(20,20,20,0.82);border:1px solid #ffffff;border-radius:9999px;display:flex;align-items:center;justify-content:center;">
      <span style="color:#fff;font-family:Arial,Helvetica,sans-serif;font-size:8.5px;font-weight:600;letter-spacing:0.2px;white-space:nowrap;padding:0 4px;">${escapeHtml(displayLabel)}</span>
    </div>
  `;

  // Tooltip on the element itself
  el.title = getAssetTooltip(asset);

  // Anchor the DOT (not the element edge) on the coordinate so DOM markers line
  // up with the GeoJSON circle layer used in the clustering path. The dot's
  // centre sits 3px below the 90x50 element's centre (top:16 + r:12 = 28 vs 25),
  // so we nudge up by 3px. Using anchor:'bottom' previously placed the dot ~14px
  // off the point — a constant pixel offset that looked large when zoomed out and
  // negligible when zoomed in (visible only once the filter dropped us below the
  // clustering threshold and onto these DOM markers).
  const marker = new maplibregl.Marker({
    element: el,
    anchor: 'center',
    offset: [0, PIN_ANCHOR_NUDGE_Y]
  })
    .setLngLat([lng, lat])
    .addTo(map);

  // Click on the rich marker element → use the shared popup helper (opens description + loads history)
  el.addEventListener('click', (ev) => {
    ev.stopPropagation();
    showAssetPopup(asset, map);
  });

  markers.set(asset.assetUUID, marker);
  return marker;
}

/**
 * Draws (or clears) the hairline that ties a fanned-out pin back to the spot it
 * actually reports. Without it the fan would quietly lie about the position.
 *
 * The line is appended to the marker element, whose own coordinate system has
 * the dot centre at (45, 28); the true point is therefore (45 - dx, 28 - dy).
 */
function setPinLeader(el, dx, dy) {
  let line = el.querySelector('.pin-leader');
  const len = Math.hypot(dx, dy);

  if (len < 1) {
    if (line) line.remove();
    return;
  }

  if (!line) {
    line = document.createElement('div');
    line.className = 'pin-leader';
    // First child, so it paints UNDER the dot and the label pill.
    el.insertBefore(line, el.firstChild);
  }

  // Angle from the dot towards the true point.
  const deg = Math.atan2(-dy, -dx) * 180 / Math.PI;
  line.style.cssText =
    'position:absolute;left:45px;top:28px;height:0;pointer-events:none;'
    + `width:${len.toFixed(1)}px;border-top:1px dashed rgba(17,24,39,0.55);`
    + `transform-origin:0 0;transform:rotate(${deg.toFixed(2)}deg);`;
}

/**
 * Fans out DOM markers that land on the same pixel, so none of them is hidden
 * underneath another. Call it after the markers are added AND on every zoom:
 * the grouping is a screen-distance question, so assets that genuinely separate
 * as you zoom in must fall back to their true positions.
 *
 * Only the DOM-marker path needs this. The cluster layer below
 * CLUSTER_MIN_ASSETS does not run, and above it a stack shows as a counted
 * circle instead.
 */
export function spreadCoLocatedMarkers(map) {
  if (!map || markers.size < 2) {
    if (map && markers.size === 1) {
      const only = markers.values().next().value;
      try {
        only.setOffset([0, PIN_ANCHOR_NUDGE_Y]);
        setPinLeader(only.getElement(), 0, 0);
      } catch (e) {}
    }
    return;
  }

  let points;
  try {
    points = [...markers.entries()].map(([id, m]) => ({ id, m, p: map.project(m.getLngLat()) }));
  } catch (e) {
    return;   // projection needs a live map; never let this break a render
  }

  // Greedy grouping around the first unclaimed member. n is small (one map
  // screen of assets), and exactness buys nothing here - the point is only to
  // notice that pins coincide.
  const claimed = new Set();
  const groups = [];
  for (const a of points) {
    if (claimed.has(a.id)) continue;
    const group = [a];
    claimed.add(a.id);
    for (const b of points) {
      if (claimed.has(b.id)) continue;
      if (Math.hypot(b.p.x - a.p.x, b.p.y - a.p.y) <= SPREAD_MIN_PX) {
        group.push(b);
        claimed.add(b.id);
      }
    }
    groups.push(group);
  }

  let fanned = 0;
  for (const group of groups) {
    const fan = group.length > 1 && group.length <= SPREAD_MAX;
    // Stable order, so the same asset keeps the same slot across re-renders
    // and the fan does not reshuffle under the user on every delta poll.
    if (fan) group.sort((x, y) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));

    group.forEach((member, i) => {
      const dy = fan ? (i - (group.length - 1) / 2) * SPREAD_PITCH_PX : 0;
      try {
        member.m.setOffset([0, dy + PIN_ANCHOR_NUDGE_Y]);
        setPinLeader(member.m.getElement(), 0, dy);
      } catch (e) {}
    });
    if (fan) fanned += group.length;
  }

  if (fanned) console.log(`[Map] Fanned out ${fanned} co-located pins`);
}

/**
 * Shows the floating asset info panel (used both from DOM marker clicks and
 * from unclustered cluster-layer clicks) and triggers the history load. The
 * panel is docked to the top-left of the map so it stays put while the camera
 * zooms out to fit the route.
 */
export function showAssetPopup(asset, map) {
  if (!asset || !map) return;

  showAssetInfoPanel(asset, map);

  // load history (route + waypoints) — non-blocking; the card already opened.
  // Reflect progress in the card's footer status line.
  setPanelHistoryStatus('loading');
  loadAssetHistoryAndDrawRoute(asset.assetUUID, map)
    .then(status => setPanelHistoryStatus(status || 'ok'))
    .catch(() => setPanelHistoryStatus('error'));
}


export function clearMarkers() {
  markers.forEach(m => {
    try { m.remove(); } catch (e) {}
  });
  markers.clear();
}
